'use es6';

import hubspot from 'hubspot';
import { editorInitialized } from 'LayoutBuilderUI/actions/CrossFrameActions';
import { render, unmountComponentAtNode } from 'react-dom';
import { createElement } from 'react';
import { reportRenderFailed } from 'LayoutBuilderUI/actions/ApiActions';
import Raven from 'raven-js';
import { setDefaultNode as setDefaultNodeDeprecated } from 'UIComponents/utils/RootNode';
import { setDefaultNode } from 'foundations-components/transitional/utils/RootNode';
import ContentRoutes from 'ContentUtils/Routes';
import { ErrorMessage } from 'foundations-components/transitional/components/ErrorMessage';
import { Paragraph } from 'foundations-components/transitional/components/Paragraph';
import { View } from 'foundations-components/transitional/components/View';
import FormattedMessage from 'I18n/components/FormattedMessage';
import configureRaven from 'raven-hubspot/configure';
import { Application } from 'foundations-components/components/Application';
import Messenger from 'LayoutBuilderUI/lib/Messenger';
import { recordNavigationTimings } from 'LayoutBuilderUI/utils/Metrics';
import { configureRavenDefaultContext } from 'EmailEditorUI/utils/raven';
import { NAMESPACE_CLASS, NamespaceWrapper } from 'EmailEditorUI/components/NamespaceWrapper';
import { jsx as _jsx } from "react/jsx-runtime";
const MANAGED_FATAL_TEMPLATE_ERRORS = [{
  message: /Missing template/,
  reason: /MISSING/
}];
const isManagedTemplateError = err => {
  return MANAGED_FATAL_TEMPLATE_ERRORS.some(managed => Object.keys(managed).every(key => err[key] && managed[key].test(err[key])));
};
export const startApp = App => {
  const messenger = new Messenger();
  window.LayoutBuilderUI = {
    messenger
  };
  const params = new URLSearchParams(window.location.search);
  window.HUBLET = params.get('hublet');
  window.hubspot.sessionId = params.get('sessionId');
  if (params.get('localAssets') === 'true') {
    window.DEPLOYED = false;
  } else {
    window.DEPLOYED = true;
  }
  if (params.get('env') === 'qa') {
    window.ENV = 'qa';
  }
  configureRaven('https://889f070dd22ca061115639b4a84843db@exceptions.hubspot.com/v2/1', {
    stacktrace: true,
    ignoreErrors: [
    // Ignore Metamask extension error
    /Failed to connect to MetaMask/i],
    tags: {
      isAcceptanceTest: params.get('seleniumRunning') === 'true',
      project: 'LayoutBuilderUI'
    }
  });
  if (window.hs === null || typeof window.hs === 'undefined') {
    window.hs = {};
  }
  if (window._hsq === null) {
    window._hsq = [];
  }
  try {
    window.hs.app = new App();
    window.hs.app.initialize();
    window.hs.app.start();
  } catch (error) {
    Raven.captureException(new Error('LayoutBuilderUI failed to start', {
      cause: error
    }));
    messenger.onAppReady(() => {
      try {
        reportRenderFailed();
      } catch (_unused) {
        // do nothing
      }
    });
  } finally {
    try {
      recordNavigationTimings({
        appName: App.name
      });
    } catch (_unused2) {
      // do nothing
    }
  }
  window.hsInEditor = true;
};
const createErrorOverlay = () => {
  const errorComponent = /*#__PURE__*/createElement(() => /*#__PURE__*/_jsx(Application, {
    name: "LayoutBuilderUI",
    children: /*#__PURE__*/_jsx(NamespaceWrapper, {
      children: /*#__PURE__*/_jsx(View, {
        position: "fixed",
        inset: "0",
        backgroundColor: "fill-surface-default",
        children: /*#__PURE__*/_jsx(ErrorMessage, {
          includeSessionId: true,
          title: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "layoutBuilder.genericError.title"
          }),
          children: /*#__PURE__*/_jsx(Paragraph, {
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "layoutBuilder.genericError.description"
            })
          })
        })
      })
    })
  }));
  const errorContainer = document.createElement('div');
  document.body.append(errorContainer);
  render(errorComponent, errorContainer);
};
export const mountApplicationWithErrorHandling = (appComponent, props, DOMElement, editorInitializedInfo = {}, extraSentryContext = {}) => {
  try {
    const app = /*#__PURE__*/_jsx(Application, {
      name: "LayoutBuilderUI",
      children: /*#__PURE__*/createElement(appComponent, props)
    });
    ensureUIComponentsRootNode();
    unmountComponentAtNode(DOMElement);
    render(app, DOMElement);
    editorInitialized(editorInitializedInfo);
  } catch (e) {
    const templateErrors = props.schema ? props.schema.template_errors : [];
    let message;
    if (templateErrors.length && templateErrors.every(item => isManagedTemplateError(item))) {
      return;
    }
    const fatalErrors = templateErrors.filter(t => t.severity === 'FATAL' && !isManagedTemplateError(t));
    if (fatalErrors.length) {
      message = `Fatal template error present for LBUI render: [${fatalErrors.map(err => err.category).sort().join(', ')}]`;
    } else if (templateErrors.length) {
      message = 'Exception during LBUI render with warning template errors present';
    } else {
      message = 'Exception during LBUI render';
    }
    createErrorOverlay();
    reportRenderFailed(fatalErrors.length > 0);
    console.error(message, templateErrors, e);
    Raven.captureException(new Error(message, {
      cause: e
    }), {
      extra: Object.assign({}, extraSentryContext, {
        isEmbedded: window !== window.top,
        readyState: document.readyState,
        visibilityState: document.visibilityState,
        templateErrors
      })
    });
  }
};
function ensureUIComponentsRootNode() {
  const rootNode = document.getElementById('uicomponents-root-node');
  if (!rootNode) {
    const newLayer = document.createElement('div');
    newLayer.id = 'uicomponents-root-node';
    newLayer.className = `${NAMESPACE_CLASS} mce-root-node`;
    document.body.appendChild(newLayer);
    setDefaultNodeDeprecated(newLayer);
    setDefaultNode(newLayer);
  }
}
export const initAppEssentials = (initData, emailTemplateMode) => {
  const {
    metaData,
    bender,
    seleniumCookie,
    initialSyncState
  } = initData;
  const {
    auth
  } = initialSyncState;
  configureRavenDefaultContext(auth, {
    extra: {
      currentProject: bender.currentProject,
      currentProjectVersion: bender.currentProjectVersion,
      emailTemplateMode,
      isCustomWithFlexArea: metaData.isCustomWithFlexArea,
      isNewEmail: !!metaData.isNewEmail,
      parentFrameURL: metaData.parentFrameURL,
      seleniumCookie
    }
  });
  hubspot.bender = bender;

  // Initialize content routes
  ContentRoutes.init(auth.gates);
};